import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';
import { useQueryClient } from '@tanstack/react-query';

import { cn, ia } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import ProgressBar from 'components/shared/ProgressBar/ProgressBar';

const Header = ({
  headerButtonProps,
  progress,
  isAdmin,
  headerOptions,
  headerInfoTooltip,
  isCompact = false,
  isNarrowScreen = false,
  onToggleSidebar,
  onExit = null
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const subtitleRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showFullTextModal, setShowFullTextModal] = useState(false);

  useEffect(() => {
    const el = subtitleRef.current;
    if (el) {
      // Temporarily remove clamp to measure true scroll height
      el.style.webkitLineClamp = 'unset';
      const overflows = el.scrollHeight > 88;
      el.style.webkitLineClamp = '';
      setIsOverflowing(overflows);
    }
  }, [headerOptions?.description]);

  const onClose = () => {
    queryClient.refetchQueries(['wizardInstanceProgress']);
    isAdmin ? navigate('/admin/practices') : navigate('/');
  };

  const handleExit = onExit || onClose;

  return (
    <>
      <div className="absolute left-0 top-0 w-full">
        <ProgressBar progress={progress} />
      </div>

      {/* Sidebar toggle icon - Only visible on narrow screens */}
      {isNarrowScreen && (
        <Tippy content="Open sidebar" className="tippy-dark arrow-small" placement="right">
          <div
            onClick={(e) => {
              e.stopPropagation();
              onToggleSidebar?.();
            }}
            className={cn(
              'absolute left-7 top-7 cursor-pointer p-0.5 hover:bg-neutral-75 lg:left-4 md:left-4 sm:left-4',
              isCompact && 'left-6 top-6'
            )}>
            <Icon icon="grid-7-linear" size={16} />
          </div>
        </Tippy>
      )}

      <div className="flex w-full justify-center">
        <div
          className={cn(
            'flex w-full flex-col text-left transition-all duration-300 ease-in-out md:w-full',
            isCompact ? 'pb-3 pt-[22px]' : 'gap-2 pt-14',
            isNarrowScreen && isCompact && 'pl-6'
          )}>
          <div className="items-top flex w-full flex-wrap justify-between">
            <div className="flex items-center gap-1.5">
              <p
                className={cn(
                  'font-500 text-neutral-900 transition-all duration-300 ease-in-out',
                  isNarrowScreen && 'text-lg',
                  isCompact ? 'pl-6 text-base font-500' : 'text-lg'
                )}>
                {headerOptions?.title}
              </p>
              {headerInfoTooltip && (
                <Tippy
                  content={headerInfoTooltip}
                  className="tippy-dark"
                  placement="bottom"
                  maxWidth={350}>
                  <div className="flex cursor-pointer items-center">
                    <Icon icon="info-circle-mono" size={isCompact ? 16 : 18} />
                  </div>
                </Tippy>
              )}
            </div>
            {headerButtonProps && (
              <div className={cn('flex flex-wrap items-center gap-2', isCompact && 'mr-8')}>
                {(ia(headerButtonProps) ? headerButtonProps : [headerButtonProps]).map(
                  (buttonProps, index) => (
                    <Button
                      key={buttonProps?.key ?? buttonProps?.text ?? index}
                      icon="add-square-mono"
                      iconColor="neutral"
                      variant="tertiary"
                      {...buttonProps}
                      size="small"
                    />
                  )
                )}
              </div>
            )}
          </div>
          <div
            className={cn(
              'transition-all duration-300 ease-in-out',
              isCompact ? 'mb-0 max-h-0 overflow-hidden opacity-0' : 'mb-5 opacity-100'
            )}>
            <div className="relative min-h-[44px]">
              <p
                ref={subtitleRef}
                className={cn(
                  'min-h-[44px] text-sm leading-[22px] text-neutral-700',
                  isOverflowing ? 'line-clamp-4' : ''
                )}>
                {headerOptions?.description}
              </p>
              {isOverflowing && !isCompact && (
                <span className="absolute bottom-0 right-0 flex items-center text-sm leading-[22px]">
                  <span
                    className="h-[22px] w-16"
                    style={{
                      background: 'linear-gradient(to right, transparent, white)'
                    }}
                  />
                  <span className="bg-white pl-0.5">
                    <span className="text-neutral-700">...</span>{' '}
                    <span
                      onClick={() => setShowFullTextModal(true)}
                      className="cursor-pointer text-primary-500 hover:text-primary-600">
                      Read more
                    </span>
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className={cn(
          'h-[1px] w-full bg-neutral-200 transition-all duration-300 ease-in-out',
          isCompact ? 'mb-2' : 'mb-5'
        )}></div>

      <Tippy className="tippy-dark cursor-pointer" content="Exit Myriad Motion" placement="left">
        <div
          onClick={handleExit}
          className={cn(
            'absolute right-7 top-7 cursor-pointer rounded-md p-0.5 hover:bg-neutral-75 sm:right-3 sm:top-5',
            isCompact && 'right-6 top-5'
          )}>
          <Icon icon="close-linear" size={20} />
        </div>
      </Tippy>

      <Confirm
        isOpen={showFullTextModal}
        handleClose={() => setShowFullTextModal(false)}
        title={headerOptions?.title}
        message={headerOptions?.description}
        primaryBtnTxt="Close"
        handleContinue={() => setShowFullTextModal(false)}
        variant="neutral"
        icon={null}
      />
    </>
  );
};

export default Header;
